import { z } from 'zod/v4'

/** The most rows a count of the audit log reads: above it the server stops counting and the screen says «more than». */
export const AUDIT_COUNT_CAP = 10_000

/** A count that never reads more than the cap: `value` never exceeds it and `capped` says the real figure is higher. */
export const boundedCountSchema = z.strictObject({
  value: z.number().int().min(0).max(AUDIT_COUNT_CAP),
  capped: z.boolean(),
})
export type BoundedCount = z.infer<typeof boundedCountSchema>

/** Opaque position in the list (the server writes and reads it; a client only sends it back). */
export const auditCursorSchema = z.string().min(1).max(200)

/**
 * One row of `bat.audit_log`. `action` and `targetType` are free text on purpose: the table also holds the
 * lower-case vocabulary the organisation admins write. No row carries the name of a person: the author is
 * `actorId` and `actorEmail` (null when the user no longer exists).
 */
export const auditEventRowSchema = z.strictObject({
  id: z.guid(),
  occurredAt: z.iso.datetime(),
  action: z.string().min(1).max(50),
  actorId: z.guid(),
  actorEmail: z.string().nullable(),
  impersonatorEmail: z.string().nullable(),
  tenantId: z.guid().nullable(),
  tenantLabel: z.string().nullable(),
  targetType: z.string().min(1).max(50),
  targetId: z.guid().nullable(),
  targetLabel: z.string().nullable(),
  ip: z.string().nullable(),
})
export type AuditEventRow = z.infer<typeof auditEventRowSchema>

/** A page read by cursor: `nextCursor` is null on the last one. */
export function cursorPageSchema<T extends z.ZodType>(row: T) {
  return z.strictObject({ items: z.array(row), nextCursor: auditCursorSchema.nullable() })
}
